import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './ScannerDashboard.css';
import { isB3TradingDay, isB3MarketOpen } from '../utils/b3Calendar';
import { getIntradayScanner, getKairi, getReversalMonitor, getRSI2, getScanner, getSetorial, getStrategyHealth } from '../api/brapi';
import { useAlertSound } from '../hooks/useAlertSound';
import WebSocketManager from './WebSocketManager';
import PortfolioModal from './PortfolioModal';
import DismissibleNotice from './DismissibleNotice';
import { addPosition } from '../api/portfolio';
import { getOptionsScanner } from '../api/options';
import { getNextOptionsUpdate } from '../utils/optionsBudget';
import { formatDateTimeBR } from '../utils/dates';

const SECTOR_EMOJI = {
  Bancos: '🏦', Petróleo: '🛢', Mineração: '⛏', Varejo: '🛒',
  Consumo: '🥩', Industrial: '⚙', Energia: '⚡', Saúde: '💊',
  Imobiliário: '🏗', ETF: '📊',
};

const SECTOR_COLOR = {
  Bancos: '#3b82f6', Petróleo: '#f59e0b', Mineração: '#94a3b8',
  Varejo: '#ec4899', Consumo: '#f97316', Industrial: '#a78bfa',
  Energia: '#facc15', Saúde: '#34d399', Imobiliário: '#60a5fa', ETF: '#818cf8',
};

const TIER_MAP = {
  ITUB4: 'S', BBDC4: 'S', SANB11: 'S', BBAS3: 'S',
  VALE3: 'S', CSNA3: 'S', GGBR4: 'S', USIM5: 'S',
  PETR4: 'A', PETR3: 'A', PRIO3: 'A', RECV3: 'A',
  ELET3: 'A', ELET6: 'A', ENGI11: 'A', EQTL3: 'A', CMIG4: 'A', CPLE6: 'A',
  ABEV3: 'A', JBSS3: 'A', BRFS3: 'A', MDIA3: 'A',
  WEGE3: 'A', EMBR3: 'A', RAIL3: 'A', VBBR3: 'A',
  LREN3: 'B', ASAI3: 'B', RADL3: 'B', ARZZ3: 'B', GRND3: 'B',
  HAPV3: 'B', RDOR3: 'B', FLRY3: 'B', DASA3: 'B',
  CYRE3: 'B', EZTC3: 'B', MRVE3: 'B', TEND3: 'B',
  VIVT3: 'B', TIMS3: 'B',
  MGLU3: 'C', AMER3: 'C', CASH3: 'C', TOTS3: 'C',
  AZUL4: 'C', GOLL4: 'C',
  BOVA11: 'ETF', SMAL11: 'ETF', IVVB11: 'ETF', FIND11: 'ETF', MATB11: 'ETF',
};

const TIER_META = {
  S:   { label: 'Tier S', desc: 'Excelentes para mean reversion',  cls: 'tier-s' },
  A:   { label: 'Tier A', desc: 'Muito bons, operar ativamente',   cls: 'tier-a' },
  B:   { label: 'Tier B', desc: 'Funcionam com ressalvas',         cls: 'tier-b' },
  C:   { label: 'Tier C', desc: 'Evitar — risco idiossincrático',  cls: 'tier-c' },
  ETF: { label: 'ETFs',   desc: 'Ideal para iniciantes',           cls: 'tier-etf' },
};
const EMPTY_TICKERS = Object.freeze([]);

const FILTERS = [
  { key: 'all',    label: 'Todos' },
  { key: 'entry',  label: 'Entradas' },
  { key: 'watch',  label: 'Acompanhar' },
  { key: 'risk',   label: 'Cuidado / saída' },
];

const SORTS = [
  { key: 'priority', label: 'Prioridade' },
  { key: 'score',    label: 'Score' },
  { key: 'ticker',   label: 'Ativo' },
  { key: 'strategy', label: 'Origem' },
];

function fmtZ(v)          { if (v == null) return '—'; return `${v > 0 ? '+' : ''}${v.toFixed(2)}σ`; }
function fmtPct(v)        { if (v == null) return '—'; return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`; }
function fmtN(v, dec = 1) { if (v == null) return '—'; return v.toFixed(dec); }
function fmtPrice(v)      { return v == null ? '—' : `R$ ${Number(v).toFixed(2).replace('.', ',')}`; }
function fmtBeta(v)       { return v != null && v !== '' && Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—'; }
function fmtDate(v)       { return v ? String(v).slice(0, 10).split('-').reverse().join('/') : null; }

const STRATEGIES = [
  { key: 'scanner',   label: 'Scanner B3',              tone: 'scanner' },
  { key: 'intraday',  label: 'Scanner Intraday 1h',     tone: 'scanner' },
  { key: 'reversal',  label: 'Reversão na Tendência',   tone: 'reversal' },
  { key: 'rsi2',      label: 'RSI(2) Connors',          tone: 'rsi2' },
  { key: 'confluence',label: 'Kairi + Confluência',     tone: 'confluence' },
  { key: 'setorial',  label: 'Análise Setorial',        tone: 'setorial' },
];

const SOURCE_LABELS = {
  scanner: 'Scanner B3',
  intraday: 'Scanner Intraday 1h',
  reversal: 'Reversão na Tendência',
  rsi2: 'RSI(2) Connors',
  confluence: 'Kairi + Confluência',
  setorial: 'Análise Setorial',
};

function pendingSourceStates() {
  return Object.fromEntries(Object.keys(SOURCE_LABELS).map(key => [key, 'pending']));
}

const KAI_PARAMS = {
  buyThreshold: -5,
  sellThreshold: 5,
  rsiThreshold: 10,
  ibsThreshold: 0.2,
  volThreshold: 1,
};

function getConfluence(asset) {
  const kairi = Number(asset.kairi);
  const rsi2 = Number(asset.rsi2);
  const ibs = Number(asset.ibs);
  const trendPct = Number(asset.trendPct);
  const volRatio = Number.isFinite(Number(asset.volRatio)) ? Number(asset.volRatio) : 1;
  const volumePass = volRatio >= KAI_PARAMS.volThreshold;
  const volumeWarn = !volumePass && volRatio >= 0.7;
  const checks = [
    kairi <= KAI_PARAMS.buyThreshold,
    rsi2 <= KAI_PARAMS.rsiThreshold,
    ibs <= KAI_PARAMS.ibsThreshold,
    trendPct > 0,
    volumePass,
  ];
  const score = checks.reduce((total, passed) => total + (passed ? 20 : 0), 0) + (volumeWarn ? 10 : 0);
  return { score, filtered: !(trendPct > 0) };
}

function classifyConfluence(asset, conf) {
  if (conf.filtered) return 'filtered';
  if (asset.kairi >= KAI_PARAMS.sellThreshold) return 'sell';
  if (conf.score >= 75 && asset.kairi <= KAI_PARAMS.buyThreshold) return 'buy';
  if (conf.score >= 60 && asset.kairi <= KAI_PARAMS.buyThreshold + 1) return 'watch';
  if (conf.score >= 40) return 'watch';
  return 'skip';
}

function connorsScore(asset) {
  return [
    asset.r1AboveMa200,
    asset.r2BelowMa5,
    asset.r3Approaching,
    asset.r4Entry,
  ].filter(Boolean).length * 25;
}

function normalizedStatus(status) {
  const map = {
    buy: ['Compra', 'buy', 'entry'],
    investigate: ['Investigar', 'investigate', 'watch'],
    danger: ['Cuidado', 'danger', 'risk'],
    watch: ['Acompanhar', 'watch', 'watch'],
    sell: ['Saída', 'danger', 'risk'],
    approaching: ['Próximo', 'investigate', 'watch'],
    ARMADO: ['Armado', 'investigate', 'entry'],
    DISPARADO: ['Disparado', 'buy', 'entry'],
    review: ['Revisar data ex', 'investigate', 'watch'],
    desatualizado: ['Dados desatualizados', 'skip', 'watch'],
  };
  const [label, tone, group] = map[status] || ['—', 'skip', 'watch'];
  return { label, tone, group };
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="sc-kpi">
      <div className="sc-kpi-label">{label}</div>
      <div className={`sc-kpi-value ${color || ''}`}>{value}</div>
      {sub && <div className="sc-kpi-sub">{sub}</div>}
    </div>
  );
}

export default function ScannerDashboard({ onAssetClick, onNewAlerts, universeTickers = EMPTY_TICKERS }) {
  const [data,       setData]      = useState(null);
  const [intradayData, setIntradayData] = useState(null);
  const [sourceData, setSourceData] = useState({ kairi: null, reversal: null, rsi2: null, setorial: null });
  const [strategyHealth, setStrategyHealth] = useState(null);
  const [sourceStates, setSourceStates] = useState(pendingSourceStates);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState(null);
  const [filter,     setFilter]    = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [tierFilter, setTierFilter]= useState('all');
  const [onlyAgreeing, setOnlyAgreeing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortKey,    setSortKey]   = useState('priority');
  const [sortAsc,    setSortAsc]   = useState(true);
  const [lastFetch,  setLastFetch] = useState(null);
  const [newAlerts,  setNewAlerts] = useState([]);
  const [liveData,   setLiveData]  = useState({});
  const [flashTickers, setFlashTickers] = useState(new Set());
  const [flashDir,     setFlashDir]     = useState({});   // { ticker: 'up'|'down' }
  const [portfolioAsset, setPortfolioAsset] = useState(null);
  const [optionsScanner, setOptionsScanner] = useState([]);

  const { muted, toggleMute, playBuy, playSell } = useAlertSound();
  const prevSignalsRef  = useRef(null);
  const prevIntradaySignalsRef = useRef(null);
  const intradayRefreshQueueRef = useRef(Promise.resolve());
  const latestIntradayBarRef = useRef(null);
  const initializedRef  = useRef(false);
  const wsmRef          = useRef(null);

  // ── helper: flash tickers with direction ─────────────────────────────────
  const flashUpdated = useCallback((entries) => {
    // entries: [{ ticker, dir }]  or legacy string array
    if (!entries || !entries.length) return;
    const normalized = entries.map(e =>
      typeof e === 'string' ? { ticker: e, dir: 'up' } : e
    );
    const tickers = normalized.map(e => e.ticker);
    const dirMap  = Object.fromEntries(normalized.map(e => [e.ticker, e.dir]));
    setFlashDir(prev => ({ ...prev, ...dirMap }));
    setFlashTickers(prev => new Set([...prev, ...tickers]));
    setTimeout(() => {
      setFlashTickers(prev => {
        const n = new Set(prev); tickers.forEach(t => n.delete(t)); return n;
      });
      setFlashDir(prev => {
        const n = { ...prev }; tickers.forEach(t => delete n[t]); return n;
      });
    }, 700);
  }, []);

  // ── Load each strategy independently, preserving its own calculation rules. ─
  const load = useCallback(async () => {
    if (!initializedRef.current) { setLoading(true); setError(null); }
    setSourceStates(previous => ({
      ...previous,
      scanner: 'pending',
      reversal: 'pending',
      rsi2: 'pending',
      confluence: 'pending',
      setorial: 'pending',
    }));
    const ensureRenderable = () => setData(previous => previous || {
      assets: [], kpis: { total: 0, aboveMm200: 0, buy: 0, investigate: 0, filtered: 0 }, sectorZ: {},
    });
    const recordFailure = (key) => {
      ensureRenderable();
      setSourceStates(previous => ({ ...previous, [key]: 'unavailable' }));
    };
    const publishScanner = (scanner) => {
      setData(scanner);
      setSourceStates(previous => ({ ...previous, scanner: 'available' }));
      setLastFetch(Date.now());
      if (!initializedRef.current) {
        setLiveData(previous => {
          const next = { ...previous };
          (scanner.assets || []).forEach(asset => {
            if (asset.close != null && !next[asset.ticker]) {
              next[asset.ticker] = { price: asset.close, change: null, source: 'db' };
            }
          });
          return next;
        });
      }

      const previousSignals = prevSignalsRef.current;
      if (previousSignals !== null && scanner.assets) {
        const alerts = [];
        let hasBuy = false;
        let hasSell = false;
        scanner.assets.forEach(asset => {
          const oldSignal = previousSignals.get(asset.ticker);
          if (asset.signal === oldSignal) return;
          if (asset.signal === 'buy') { hasBuy = true; alerts.push({ ticker: asset.ticker, type: 'buy', label: 'Compra' }); }
          else if (asset.signal === 'danger') { hasSell = true; alerts.push({ ticker: asset.ticker, type: 'sell', label: 'Cuidado' }); }
          else if (asset.signal === 'investigate' && oldSignal !== 'buy') {
            hasBuy = true; alerts.push({ ticker: asset.ticker, type: 'investigate', label: 'Investigar' });
          }
        });
        if (alerts.length && isB3TradingDay()) {
          const eventTime = Date.now();
          setNewAlerts(alerts.map((alert, index) => ({ ...alert, eventKey: `${eventTime}:${index}` })));
          setTimeout(() => setNewAlerts([]), 8000);
          if (hasBuy) playBuy();
          if (hasSell && !hasBuy) playSell();
          onNewAlerts?.(alerts);
        }
      }
      prevSignalsRef.current = new Map((scanner.assets || []).map(asset => [asset.ticker, asset.signal]));
      initializedRef.current = true;
    };
    const publishSource = (key, value) => {
      ensureRenderable();
      setSourceData(previous => ({ ...previous, [key]: value }));
      setSourceStates(previous => ({ ...previous, [key]: 'available' }));
      setLastFetch(Date.now());
    };

    const requests = [
      getOptionsScanner().then(setOptionsScanner).catch(() => setOptionsScanner([])),
      getScanner().then(publishScanner).catch(() => recordFailure('scanner')),
      getReversalMonitor().then(value => publishSource('reversal', value)).catch(() => recordFailure('reversal')),
      getRSI2(universeTickers.join(',')).then(value => publishSource('rsi2', value)).catch(() => recordFailure('rsi2')),
      getKairi(universeTickers.join(',')).then(value => publishSource('kairi', value)).catch(() => recordFailure('confluence')),
      getSetorial().then(value => publishSource('setorial', value)).catch(() => recordFailure('setorial')),
      getStrategyHealth().then(setStrategyHealth).catch(() => setStrategyHealth(null)),
    ];

    await Promise.all(requests);
    setLoading(false);
  }, [playBuy, playSell, onNewAlerts, universeTickers]);

  const loadIntraday = useCallback(async () => {
    const refresh = async () => {
      try {
       setSourceStates(previous => ({ ...previous, intraday: 'pending' }));
      const intraday = await getIntradayScanner();
      const barTime = intraday.barTime ? Date.parse(intraday.barTime) : null;
      const latestBar = latestIntradayBarRef.current;
      // Uma resposta atrasada nunca pode substituir o snapshot mais novo e
      // restaurar um estado que já havia gerado alerta.
      if ((barTime == null && latestBar != null) || (barTime != null && latestBar != null && barTime < latestBar)) return;
      if (barTime != null) latestIntradayBarRef.current = barTime;
      setIntradayData(intraday);
       setSourceStates(previous => ({ ...previous, intraday: 'available' }));
      const previousSignals = prevIntradaySignalsRef.current;
      const signals = intraday.assets || [];
      if (previousSignals !== null) {
        const alerts = [];
        signals.forEach(asset => {
          const oldSignal = previousSignals.get(asset.ticker);
          if (!oldSignal || oldSignal === asset.signal) return;
          if (asset.signal === 'buy') alerts.push({ ticker: asset.ticker, type: 'buy', label: 'Entrada intraday' });
          else if (asset.signal === 'danger') alerts.push({ ticker: asset.ticker, type: 'sell', label: 'Cuidado intraday' });
          else if (asset.signal === 'investigate' && oldSignal !== 'buy') alerts.push({ ticker: asset.ticker, type: 'investigate', label: 'Investigar intraday' });
        });
        if (alerts.length && isB3TradingDay()) {
          const eventTime = Date.now();
          setNewAlerts(alerts.map((alert, index) => ({ ...alert, eventKey: `${eventTime}:${index}` })));
          setTimeout(() => setNewAlerts([]), 8000);
          if (alerts.some(alert => alert.type !== 'sell')) playBuy();
          else playSell();
          onNewAlerts?.(alerts);
        }
      }
      prevIntradaySignalsRef.current = new Map(signals.map(asset => [asset.ticker, asset.signal]));
      } catch (_) {
        setIntradayData(previous => previous || { status: 'unavailable', assets: [] });
         setSourceStates(previous => ({ ...previous, intraday: 'unavailable' }));
      }
    };
    // O timer e o WebSocket podem pedir atualização ao mesmo tempo. Enfileirar
    // evita comparar sinais com respostas HTTP que chegaram fora de ordem.
    const queuedRefresh = intradayRefreshQueueRef.current.catch(() => {}).then(refresh);
    intradayRefreshQueueRef.current = queuedRefresh;
    return queuedRefresh;
  }, [onNewAlerts, playBuy, playSell]);

  // Strategy data refreshes every five minutes; live prices still update below.
  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  // A API intraday já entrega somente candles horários fechados; buscar o estado
  // com frequência mantém o painel sincronizado sem repetir coleta na fonte.
  useEffect(() => {
    loadIntraday();
    const id = setInterval(loadIntraday, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [loadIntraday]);

  useEffect(() => {
    window.addEventListener('fatcat:intraday-reload', loadIntraday);
    return () => window.removeEventListener('fatcat:intraday-reload', loadIntraday);
  }, [loadIntraday]);

  // ── Silent price poll every 10 s via HTTP (WebSocket fallback) ───────────
  useEffect(() => {
    let active = true;
    async function pollPrices() {
      try {
        const r = await fetch('/api/prices/live');
        if (!r.ok || !active) return;
        const payload = await r.json();
        const prices = Array.isArray(payload) ? payload : (payload.prices || []);
        const entries = [];
        setLiveData(prev => {
          const next = { ...prev };
          prices.forEach(({ ticker, price, change }) => {
            if (price == null) return;
            const old = prev[ticker];
            if (!old || old.price !== price) {
              const dir = old ? (price > old.price ? 'up' : 'down') : 'up';
              next[ticker] = { price, change: change ?? old?.change ?? null, source: 'poll' };
              if (old) entries.push({ ticker, dir });
            }
          });
          setTimeout(() => flashUpdated(entries), 0);
          return next;
        });
      } catch (_) {}
    }
    pollPrices();
    // Keep quotes fresh during the B3 session without turning this into a
    // second strategy refresh loop. The initial snapshot above is intentional.
    const id = setInterval(() => {
      if (isB3MarketOpen()) pollPrices();
    }, 15 * 60 * 1000);
    return () => { active = false; clearInterval(id); };
  }, [flashUpdated]);

  // WebSocket — cotação ao vivo
  const handleWsMessage = useCallback((priceData) => {
    const updated = [];
    setLiveData(prev => {
      const next = { ...prev };
      priceData.forEach(({ ticker, value, variation }) => {
        const t = ticker.replace('.SA', '');
        next[t] = { price: parseFloat(value), change: parseFloat(variation) };
        updated.push(t);
      });
      return next;
    });
    if (updated.length > 0) {
      setFlashTickers(prev => new Set([...prev, ...updated]));
      setTimeout(() => {
        setFlashTickers(prev => {
          const next = new Set(prev);
          updated.forEach(t => next.delete(t));
          return next;
        });
      }, 600);
    }
  }, []);

  useEffect(() => {
    const wsm = new WebSocketManager(handleWsMessage, () => {});
    wsm.connect();
    wsmRef.current = wsm;
    return () => wsm.close();
  }, [handleWsMessage]);

  // Subscrever tickers quando os dados carregarem
  useEffect(() => {
    if (!data || !wsmRef.current) return;
    const symbols = data.assets.map(a => `${a.ticker}|S`);
    wsmRef.current.subscribeToSymbols(symbols);
  }, [data]);

  const consolidatedRows = useMemo(() => {
    const rows = [];
    const scannerRiskByTicker = new Map(
      (data?.assets || []).map(asset => [asset.ticker, {
        hasOptions: asset.hasOptions,
        betaSector: asset.betaSector,
        betaIbovespa: asset.betaIbovespa,
        tier: asset.tier || TIER_MAP[asset.ticker] || null,
      }])
    );
    const add = row => {
      const risk = scannerRiskByTicker.get(row.ticker) || {};
      const tier = row.tier || risk.tier || TIER_MAP[row.ticker] || null;
      const tierHealthKey = row.strategyKey === 'rsi2'
        ? (tier === 'SC' ? 'connorsSc' : 'connorsBlue')
        : row.strategyKey === 'confluence'
        ? (tier === 'SC' ? 'confluenceSc' : 'confluenceBlue')
        : row.strategyKey;
      const strategyHealthEntry = strategyHealth?.strategies?.[tierHealthKey]
        || strategyHealth?.strategies?.[row.strategyKey];
      const healthGatedStrategy = ['rsi2', 'confluence', 'setorial', 'reversal'].includes(row.strategyKey);
      const riskRewardBlocked = row.strategyKey === 'reversal'
        && Number.isFinite(Number(row.riskReward))
        && Number(row.riskReward) < 1;
      const healthBlocked = healthGatedStrategy && strategyHealthEntry?.blocked !== false;
      const rowBlocks = Array.isArray(row.blocks) ? row.blocks : [];
      rows.push({
        id: `${row.strategyKey}-${row.ticker}-${row.status}`,
        ...row,
        hasOptions: row.hasOptions ?? risk.hasOptions ?? null,
        betaSector: row.betaSector ?? risk.betaSector ?? null,
        betaIbovespa: row.betaIbovespa ?? risk.betaIbovespa ?? null,
        tier,
        riskRewardBlocked,
        strategyHealth: strategyHealthEntry,
        requiresOverride: healthBlocked,
        entryBlocked: riskRewardBlocked || rowBlocks.length > 0,
        entryBlockTitle: riskRewardBlocked
          ? 'R/R < 1 — não operar'
          : rowBlocks.length > 0
          ? rowBlocks.join(' · ')
          : strategyHealthEntry?.available === false
          ? 'Saúde da estratégia indisponível — entrada bloqueada'
          : 'Estratégia com expectativa negativa — bloqueada',
      });
    };
    const sectorByTicker = new Map();
    (data?.assets || []).forEach(asset => sectorByTicker.set(asset.ticker, asset.sector));
    (sourceData.setorial?.allAssets || []).forEach(asset => {
      if (asset.sectorName) sectorByTicker.set(asset.ticker, asset.sectorName);
    });
    const kairiReferenceDate = sourceData.kairi?.referenceDate || null;
    const rsi2ReferenceDate = sourceData.rsi2?.referenceDate || null;
    const dailySourcesAligned = !kairiReferenceDate
      || !rsi2ReferenceDate
      || kairiReferenceDate === rsi2ReferenceDate;
    const kairiByTicker = new Map(
      (sourceData.kairi?.assets || []).map(asset => [asset.ticker, asset]),
    );
    const rsi2ByTicker = new Map(
      (sourceData.rsi2?.assets || []).map(asset => [asset.ticker, asset]),
    );

    (data?.assets || [])
      .filter(asset => asset.signal && asset.signal !== 'skip')
      .forEach(asset => {
        const status = normalizedStatus(asset.signal);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.close,
          strategyKey: 'scanner', strategy: 'Scanner B3', status: asset.signal,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: null, context: `Z-Kairi ${fmtZ(asset.zKairi)} · RSI(2) ${fmtN(asset.rsi2)}`,
          hasOptions: asset.hasOptions, betaSector: asset.betaSector, betaIbovespa: asset.betaIbovespa,
          needsReview: asset.needsReview === true,
          portfolio: { refs: [{ label: 'Z-Kairi', value: fmtZ(asset.zKairi), highlight: true }, { label: 'RSI(2)', value: fmtN(asset.rsi2) }] },
          priority: asset.signal === 'buy' ? 0 : asset.signal === 'investigate' ? 1 : asset.signal === 'watch' ? 2 : 3,
        });
      });

    (intradayData?.assets || [])
      .filter(asset => asset.signal && asset.signal !== 'skip')
      .forEach(asset => {
        const status = normalizedStatus(asset.signal);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.close,
          strategyKey: 'intraday', strategy: 'Scanner Intraday 1h', status: asset.signal,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: null,
          context: `Candle 1h fechado · Z-Kairi ${fmtZ(asset.zKairi)} · RSI(2) ${fmtN(asset.rsi2)}`,
          hasOptions: asset.hasOptions, betaSector: asset.betaSector, betaIbovespa: asset.betaIbovespa,
          portfolio: { refs: [{ label: 'Z-Kairi intraday', value: fmtZ(asset.zKairi), highlight: true }, { label: 'RSI(2) intraday', value: fmtN(asset.rsi2) }] },
          priority: asset.signal === 'buy' ? 0 : asset.signal === 'investigate' ? 1 : asset.signal === 'watch' ? 2 : 3,
          intraday: true,
          blocks: asset.dadosDesatualizados ? (asset.isPartial ? ['Dados desatualizados — leitura parcial'] : ['Dados desatualizados — candle com mais de 1 pregão']) : undefined,
        });
      });

    [...(sourceData.reversal?.triggered || []), ...(sourceData.reversal?.armed || [])]
      .forEach(asset => {
        const status = normalizedStatus(asset.status);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.close,
          strategyKey: 'reversal', strategy: 'Reversão na Tendência', status: asset.status,
          riskReward: asset.riskReward,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: asset.score, scoreMax: asset.scoreMax, conditions: asset.conditions, blocks: asset.blocks,
          needsReview: asset.needsReview === true,
          context: `Score ${asset.score ?? '—'}/${asset.scoreMax ?? 100} · RSI(2) ${fmtN(asset.rsi2)} · R/R ${asset.riskReward != null ? `${fmtN(asset.riskReward, 2)}R` : '—'}`,
          portfolio: {
            refs: [{ label: 'RSI(2)', value: fmtN(asset.rsi2), highlight: true }, { label: 'Risco / retorno', value: asset.riskReward != null ? `${fmtN(asset.riskReward, 2)}R` : '—' }],
            entryPrice: asset.entryPrice ?? asset.close, stop: asset.stopPrice, target: asset.target1Price,
          },
          priority: asset.status === 'DISPARADO' ? 0 : 1,
        });
      });

    (sourceData.rsi2?.assets || [])
      .filter(asset => asset.status && asset.status !== 'excluded')
      .forEach(asset => {
        const status = normalizedStatus(asset.status);
        const score = connorsScore(asset);
        const matchingKairi = dailySourcesAligned ? kairiByTicker.get(asset.ticker) : null;
        const kairiContext = matchingKairi ? `Kairi ${fmtPct(matchingKairi.kairi)} · ` : '';
        add({
          ticker: asset.ticker, name: asset.name, sector: sectorByTicker.get(asset.ticker) || 'Não informado', price: asset.price,
          strategyKey: 'rsi2', strategy: 'RSI(2) Connors', status: asset.status,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score, context: `${kairiContext}RSI(2) ${fmtN(asset.rsi2)} · ${asset.rulesCount ?? score / 25}/4 regras`,
          portfolio: {
            refs: [
              { label: 'Score Connors', value: `${score}%`, highlight: true },
              ...(matchingKairi ? [{ label: 'Kairi', value: fmtPct(matchingKairi.kairi) }] : []),
              { label: 'RSI(2)', value: fmtN(asset.rsi2) },
            ],
            entryPrice: asset.price, stop: asset.stopPrice, target: asset.exitTarget,
          },
          priority: asset.status === 'buy' ? 0 : asset.status === 'approaching' ? 2 : 3,
        });
      });

    (dailySourcesAligned ? sourceData.kairi?.assets || [] : [])
      .forEach(sourceAsset => {
        const matchingRsi2 = rsi2ByTicker.get(sourceAsset.ticker);
        const asset = matchingRsi2
          ? { ...sourceAsset, rsi2: matchingRsi2.rsi2 }
          : sourceAsset;
        const conf = getConfluence(asset);
        const result = classifyConfluence(asset, conf);
        if (result === 'skip' || result === 'filtered') return;
        const status = normalizedStatus(result);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sector, price: asset.price,
          strategyKey: 'confluence', strategy: 'Kairi + Confluência', status: result,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: conf.score, context: `Kairi ${fmtPct(asset.kairi)} · RSI(2) ${fmtN(asset.rsi2)}`,
          portfolio: {
            refs: [
              { label: 'Score de confluência', value: `${conf.score}%`, highlight: true },
              { label: 'Kairi', value: fmtPct(asset.kairi) },
              { label: 'RSI(2)', value: fmtN(asset.rsi2) },
            ],
            entryPrice: asset.price,
            signalScore: conf.score,
          },
          priority: result === 'buy' ? 0 : result === 'watch' ? 2 : 4,
        });
      });

    (sourceData.setorial?.allAssets || [])
      .filter(asset => ['buy', 'investigate', 'danger', 'watch'].includes(asset.signal))
      .forEach(asset => {
        const status = normalizedStatus(asset.signal);
        add({
          ticker: asset.ticker, name: asset.name, sector: asset.sectorName, price: asset.price,
          strategyKey: 'setorial', strategy: 'Análise Setorial', status: asset.signal,
          statusLabel: status.label, statusTone: status.tone, group: status.group,
          score: null, context: `Z-Ativo ${fmtZ(asset.zScore)} · Gap ${asset.gap != null ? `${fmtN(asset.gap, 2)}σ` : '—'}`,
          portfolio: { refs: [{ label: 'Z do ativo', value: fmtZ(asset.zScore), highlight: true }, { label: 'Gap do setor', value: asset.gap != null ? `${fmtN(asset.gap, 2)}σ` : '—' }], entryPrice: asset.price },
          priority: asset.signal === 'buy' ? 0 : asset.signal === 'investigate' ? 1 : asset.signal === 'watch' ? 2 : 3,
        });
      });
    return rows;
  }, [data, intradayData, sourceData, strategyHealth]);

  // Divergente = entrada e risco no MESMO ativo, avaliado sobre todas as estratégias (antes dos filtros).
  const divergentTickers = useMemo(() => {
    const flags = new Map();
    consolidatedRows.forEach(row => {
      const f = flags.get(row.ticker) || { entry: false, risk: false };
      if (row.group === 'entry') f.entry = true;
      if (row.group === 'risk') f.risk = true;
      flags.set(row.ticker, f);
    });
    return new Set([...flags].filter(([, f]) => f.entry && f.risk).map(([t]) => t));
  }, [consolidatedRows]);

  const filtered = useMemo(() => {
    let rows = [...consolidatedRows];
    if (onlyAgreeing) rows = rows.filter(row => !divergentTickers.has(row.ticker));
    if (strategyFilter !== 'all') rows = rows.filter(row => row.strategyKey === strategyFilter);
    if (filter === 'entry') rows = rows.filter(row => row.group === 'entry');
    if (filter === 'watch') rows = rows.filter(row => row.group === 'watch');
    if (filter === 'risk') rows = rows.filter(row => row.group === 'risk');
    if (tierFilter !== 'all') rows = rows.filter(row => (TIER_MAP[row.ticker] || 'B') === tierFilter);
    const byTicker = new Map();
    rows.forEach(row => {
      if (!byTicker.has(row.ticker)) byTicker.set(row.ticker, []);
      byTicker.get(row.ticker).push(row);
    });
    const groups = [...byTicker.values()].map(items => {
      const sorted = [...items].sort((x, y) => x.priority - y.priority);
      const primary = sorted[0];
      const divergent = divergentTickers.has(primary.ticker);
      return {
        ticker: primary.ticker,
        primary,
        rows: sorted,
        divergent,
        reasons: divergent ? sorted.filter(r => r.group === 'entry' || r.group === 'risk') : [],
        priority: primary.priority,
        score: Math.max(...sorted.map(r => r.score ?? -1)),
        strategy: primary.strategy,
      };
    });
    groups.sort((a, b) => {
      const values = {
        priority: [a.priority, b.priority],
        score: [a.score, b.score],
        ticker: [a.ticker, b.ticker],
        strategy: [a.strategy, b.strategy],
      };
      const [va, vb] = values[sortKey] || values.priority;
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return a.ticker.localeCompare(b.ticker);
    });
    return groups;
  }, [consolidatedRows, divergentTickers, filter, strategyFilter, tierFilter, onlyAgreeing, sortKey, sortAsc]);

  const optionsBySymbol = useMemo(
    () => new Map((Array.isArray(optionsScanner) ? optionsScanner : []).map(item => [item.symbol || item.ticker, item])),
    [optionsScanner],
  );

  function toggleSort(key) {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(true); }
  }

  if (loading && !data) return (
    <div className="sc-root">
      <div className="sc-loading"><div className="sc-spinner" /><span>Carregando scanner…</span></div>
    </div>
  );
  if (error) return (
    <div className="sc-root">
      <div className="sc-error">⚠ {error} <button onClick={load}>Tentar novamente</button></div>
    </div>
  );
  if (!data) return null;

  const { kpis } = data;
  const entriesCount = consolidatedRows.filter(row => row.group === 'entry').length;
  const strategyCounts = Object.fromEntries(
    STRATEGIES.map(strategy => [strategy.key, consolidatedRows.filter(row => row.strategyKey === strategy.key).length])
  );
  const activeRefinementCount = Number(strategyFilter !== 'all') + Number(tierFilter !== 'all');
  const sourceStatuses = Object.entries(SOURCE_LABELS).map(([key, label]) => ({
    key,
    label,
    state: sourceStates[key] || 'pending',
  }));
  const unavailableSources = sourceStatuses
    .filter(source => source.state === 'unavailable')
    .map(source => source.label);
  const optionsInfo = (Array.isArray(optionsScanner) ? optionsScanner[0] : null);
  const optionsCallsToday = Number(optionsInfo?.brapi_calls_today || 0);
  const nextOptionsUpdate = optionsInfo?.next_options_update || getNextOptionsUpdate(new Date(), optionsCallsToday);

  return (
    <div className="sc-root">

      {newAlerts.length > 0 && (
        <div className="sc-alert-toast" aria-live="polite">
          {newAlerts.map((a, i) => (
            <DismissibleNotice
              key={`${a.ticker}-${a.type}-${a.eventKey || i}`}
              noticeKey={`scanner-signal-${a.ticker}-${a.type}`}
              eventKey={a.eventKey || `${a.ticker}:${a.type}:${a.label}`}
              className={`sc-alert-item sc-alert-${a.type}`}
              closeLabel={`Fechar alerta de ${a.ticker}`}
            >
              <span className="sc-alert-icon">{a.type === 'buy' ? '🟢' : a.type === 'sell' ? '🔴' : '🟡'}</span>
              <span className="sc-alert-ticker">{a.ticker}</span>
              <span className="sc-alert-label">{a.label}</span>
            </DismissibleNotice>
          ))}
        </div>
      )}

      {/* Consolidated strategy summary */}
      <div className="sc-kpis">
        <KpiCard label="Ocorrências" value={consolidatedRows.length} />
        <KpiCard label="Entradas" value={entriesCount} color={entriesCount > 0 ? 'green' : 'muted'} />
        <KpiCard label="Estratégias" value={STRATEGIES.filter(strategy => strategyCounts[strategy.key] > 0).length} color="green" />
        <KpiCard label="Avaliáveis hoje" value={kpis.total} sub={`de ${universeTickers.length || data.marketData?.universeSize || '—'} no universo`} />
      </div>
      <div className="sc-intraday-status ready" role="note">
        <p>Bloqueio reavaliado diariamente com o backtest ponderado (meia-vida 126 pregões).</p>
      </div>
      <DismissibleNotice
        noticeKey="scanner-intraday-status"
        eventKey={JSON.stringify({
          status: intradayData?.status,
          barTime: intradayData?.barTime,
          unavailable: intradayData?.coverage?.unavailable,
        })}
        className={`sc-intraday-status${intradayData?.status === 'ready' ? ' ready' : ''}`}
        closeLabel="Fechar aviso da leitura intraday"
        role="status"
      >
        <div>
          <span>Leitura intraday</span>
          <strong>1h · candle fechado</strong>
        </div>
        <p>
          {intradayData?.status === 'ready' && intradayData.barTime
            ? `${intradayData.dadosDesatualizados ? 'Dados desatualizados — sem Compra. ' : ''}Último candle: ${formatDateTimeBR(intradayData.barTime)}${intradayData.coverage?.unavailable ? ` · ${intradayData.coverage.unavailable} ativo indisponível` : ''}`
            : intradayData?.status === 'unavailable'
              ? 'Leitura intraday indisponível no momento.'
              : 'Aguardando o próximo candle horário fechado.'}
        </p>
      </DismissibleNotice>

      <DismissibleNotice
        noticeKey="scanner-source-status"
        eventKey={sourceStatuses
          .filter(source => source.state === 'unavailable')
          .map(source => source.key)
          .sort()
          .join(',') || 'healthy'}
        className="sc-source-status"
        aria-label="Status das fontes do scanner"
        closeLabel="Fechar status das fontes"
        role="status"
      >
        <span className="sc-source-status-label">Fontes consolidadas</span>
         <span className="sc-source-status-label" data-testid="brapi-options-budget-status">BRAPI opções hoje: {optionsCallsToday}/64 chamadas{nextOptionsUpdate ? ` · Próxima: ${nextOptionsUpdate}` : ''}</span>
        <div className="sc-source-status-list">
          {sourceStatuses.map(source => {
            const stateLabel = source.state === 'unavailable' ? 'Indisponível' : source.state === 'available' ? 'Disponível' : 'Aguardando';
            return (
              <span key={source.key} className={`sc-source-chip ${source.state}`} data-testid={`source-status-${source.key}`}>
                <i aria-hidden="true" />
                <span>{source.label}</span>
                <b>{stateLabel}</b>
              </span>
            );
          })}
        </div>
      </DismissibleNotice>

      {data.risk && (
        <DismissibleNotice
          noticeKey="scanner-risk-context"
          eventKey="risk-context"
          className="sc-risk-context"
          closeLabel="Fechar aviso de risco"
          role="status"
        >
          <strong>β de risco</strong>
          <span>
            {data.risk.windowStart && data.risk.windowEnd
              ? `Retornos diários de ${fmtDate(data.risk.windowStart)} a ${fmtDate(data.risk.windowEnd)}. `
              : 'Retornos diários do último ano. '}
            Setor = carteira equiponderada dos demais ativos do setor · IBOV = Ibovespa.
            Beta mede sensibilidade histórica, não é recomendação de investimento.
          </span>
        </DismissibleNotice>
      )}

      {/* Controls */}
      <div className="sc-controls">
        <div className="sc-filters">
          {FILTERS.map(f => (
            <button key={f.key} className={`sc-filter-btn${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
              {f.key === 'entry' && entriesCount > 0 &&
                <span className="sc-filter-badge">{entriesCount}</span>
              }
            </button>
          ))}
        </div>
        <label className="sc-agree-toggle">
          <input type="checkbox" checked={onlyAgreeing} onChange={e => setOnlyAgreeing(e.target.checked)} />
          Só concordantes
        </label>
        <div className="sc-sorts">
          {SORTS.map(s => (
            <button key={s.key} className={`sc-sort-btn${sortKey === s.key ? ' active' : ''}`} onClick={() => toggleSort(s.key)}>
              {s.label} {sortKey === s.key ? (sortAsc ? '↑' : '↓') : ''}
            </button>
          ))}
        </div>
        <div className="sc-controls-right">
          <button
            className={`sc-refine-toggle${filtersOpen || activeRefinementCount ? ' active' : ''}`}
            onClick={() => setFiltersOpen(open => !open)}
            aria-expanded={filtersOpen}
            aria-controls="scanner-refinement-filters"
          >
            Filtros
            {activeRefinementCount > 0 && <span>{activeRefinementCount}</span>}
            <i>{filtersOpen ? '⌃' : '⌄'}</i>
          </button>
          <button className={`sc-sound-btn${muted ? ' muted' : ''}`} onClick={toggleMute} title={muted ? 'Som desativado' : 'Som ativado'}>
            {muted ? '🔕' : '🔔'}
          </button>
          <button className="sc-refresh-btn" onClick={load} disabled={loading}>↻ Atualizar</button>
        </div>
      </div>

      {filtersOpen && (
        <div id="scanner-refinement-filters" className="sc-refinement-panel">
          <div className="sc-filter-group">
            <span className="sc-filter-group-label">Origem do sinal</span>
            <div className="sc-strategy-options" aria-label="Filtrar por origem do sinal">
              <button className={`sc-strategy-filter${strategyFilter === 'all' ? ' active' : ''}`} onClick={() => setStrategyFilter('all')}>
                Todas <span>{consolidatedRows.length}</span>
              </button>
              {STRATEGIES.map(strategy => (
                <button
                  key={strategy.key}
                  className={`sc-strategy-filter ${strategy.tone}${strategyFilter === strategy.key ? ' active' : ''}`}
                  onClick={() => setStrategyFilter(strategy.key)}
                >
                  {strategy.label} <span>{strategyCounts[strategy.key]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="sc-filter-group sc-risk-filter-group">
            <span className="sc-filter-group-label">Risco</span>
            <div className="sc-tier-options">
              <button className={`sc-tier-btn${tierFilter === 'all' ? ' active' : ''}`} onClick={() => setTierFilter('all')}>Todos</button>
              {Object.entries(TIER_META).map(([key, meta]) => (
                <button key={key} className={`sc-tier-btn ${meta.cls}${tierFilter === key ? ' active' : ''}`}
                  onClick={() => setTierFilter(tierFilter === key ? 'all' : key)} title={meta.desc}>
                  {meta.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {unavailableSources.length > 0 && (
        <div className="sc-source-warning">
          Dados temporariamente indisponíveis: {unavailableSources.join(', ')}. As demais fontes continuam visíveis.
        </div>
      )}

      {/* Table */}
      <div className="sc-table-wrap">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Ativo</th>
              <th>Cotação</th>
              <th>Origem do sinal</th>
              <th>Setor</th>
              <th>Opções</th>
              <th title="Beta calculado com retornos diários dos últimos 12 meses">Beta (1 ano)</th>
              <th>Contexto</th>
              <th onClick={() => toggleSort('score')} className="sc-th-sort">Score / progresso</th>
              <th>Status</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {filtered.map(group => {
              const a = group.primary;
              const sectorColor = SECTOR_COLOR[a.sector] || '#4a5a75';
              const live = liveData[a.ticker];
              const price = live?.price ?? a.price;
              return (
                <tr key={group.ticker}
                  className={`sc-row${group.rows.some(r => r.group === 'entry') ? ' row-buy' : ''}${group.divergent ? ' sc-row-divergent' : ''}${onAssetClick ? ' sc-row-clickable' : ''}`}
                  onClick={() => onAssetClick && onAssetClick(a.ticker, a.strategyKey === 'intraday' ? 'scanner' : a.strategyKey)}
                >
                  {/* Ativo */}
                  <td className="sc-td-ativo">
                    <div className="sc-ticker-row">
                      <span className="sc-ticker">{a.ticker}</span>
                      {TIER_MAP[a.ticker] && (
                        <span className={`sc-tier-badge ${TIER_META[TIER_MAP[a.ticker]]?.cls}`}>
                          {TIER_MAP[a.ticker]}
                        </span>
                      )}
                    </div>
                    <div className="sc-name">{(a.name || '').substring(0, 16)}</div>
                  </td>

                  <td className="sc-td-preco">
                    <span className={`sc-preco${flashTickers.has(a.ticker) ? ` sc-flash-${flashDir[a.ticker] || 'up'}` : ''}`}>{fmtPrice(price)}</span>
                    {live?.change != null && <span className={`sc-var ${live.change >= 0 ? 'up' : 'down'}`}>{live.change >= 0 ? '+' : ''}{live.change.toFixed(2).replace('.', ',')}%</span>}
                  </td>

                  <td>
                    {group.rows.map(r => (
                      <div key={r.id} className="sc-strat-line">
                        <span className={`sc-strategy-badge ${STRATEGIES.find(item => item.key === r.strategyKey)?.tone || ''}`}>{r.strategy}</span>
                      </div>
                    ))}
                  </td>

                  {/* Setor */}
                  <td className="sc-td-setor">
                    <div className="sc-sector-pill" style={{ background: `${sectorColor}18`, borderColor: `${sectorColor}35` }}>
                      <span className="sc-sector-icon">{SECTOR_EMOJI[a.sector] || '📈'}</span>
                      <span className="sc-sector-name">{a.sector}</span>
                    </div>
                  </td>

                  <td className="sc-td-options">
                    {(() => {
                      const option = optionsBySymbol.get(a.ticker);
                       const state = option?.state || (a.hasOptions ? 'disponível' : 'não_atualizado');
                       const hv = Number(option?.hv_percentile);
                       const hvLabel = Number.isFinite(hv) ? `HV pct ${Math.round(hv)}` : null;
                       const count = Number(option?.liquid_contracts);
                       const stateLabel = hvLabel
                         ? `${hvLabel} · ${state === 'disponível' ? `${count || 0} contratos` : 'cadeia indisponível'}`
                         : 'sem dados';
                      return <span className={`sc-options-badge ${state}`} title={option?.reason || ''}>
                         {stateLabel}
                      </span>;
                    })()}
                  </td>

                  <td className="sc-td-beta" title="Beta mede a sensibilidade histórica do ativo; não indica compra ou venda.">
                    <div className="sc-beta-values">
                      <span>Setor <strong>{fmtBeta(a.betaSector)}</strong></span>
                      <span>IBOV <strong>{fmtBeta(a.betaIbovespa)}</strong></span>
                    </div>
                  </td>

                  <td className="sc-context">
                    {group.divergent && (
                      <div className="sc-divergent" role="alert">
                        <strong>⚠ Sinais divergentes</strong>
                        <ul>
                          {group.reasons.map(r => (
                            <li key={r.id}>{r.strategy}: {r.statusLabel} — {r.context}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {group.rows.map(r => (
                      <div key={r.id} className="sc-strat-line">
                        {r.context}
                        {r.riskRewardBlocked && <span className="sc-risk-seal">R/R &lt; 1</span>}
                      </div>
                    ))}
                  </td>
                  <td>{group.rows.map(r => <div key={r.id} className="sc-strat-line"><span className="sc-score-value">{r.score != null ? `${r.score}%` : '—'}</span></div>)}</td>
                  <td>{group.rows.map(r => <div key={r.id} className="sc-strat-line"><span className={`sc-signal ${r.statusTone}`}>{r.group === 'entry' && <span className="sc-signal-dot">●</span>}{r.statusLabel}</span></div>)}</td>
                  <td className="sc-actions">
                    {group.rows.map(r => !r.needsReview && (
                      <div key={r.id} className="sc-strat-line">
                        <button type="button" className="sc-add-portfolio-btn" onClick={event => { event.stopPropagation(); setPortfolioAsset({ ...r, price }); }} disabled={r.entryBlocked} title={r.entryBlocked ? r.entryBlockTitle : r.requiresOverride ? 'Exige confirmação de operação por sua conta.' : undefined}>+ Carteira</button>
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="10" className="sc-empty-row">Nenhum setup encontrado para os filtros selecionados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {portfolioAsset && (
        <PortfolioModal
          ticker={portfolioAsset.ticker}
          name={portfolioAsset.name || portfolioAsset.ticker}
          strategy={portfolioAsset.strategy}
          refs={[{ label: 'Cotação', value: fmtPrice(portfolioAsset.price) }, ...(portfolioAsset.portfolio?.refs || [])]}
          defaultEntryPrice={portfolioAsset.portfolio?.entryPrice ?? portfolioAsset.price}
          suggestedStop={portfolioAsset.portfolio?.stop}
          suggestedTarget={portfolioAsset.portfolio?.target}
          enforceMinRiskReward={portfolioAsset.strategyKey === 'reversal'}
          requiresOverride={portfolioAsset.requiresOverride}
          strategyHealth={portfolioAsset.strategyHealth}
          onClose={() => setPortfolioAsset(null)}
          onSave={async (formData) => {
            await addPosition({
              ticker: portfolioAsset.ticker,
              name: portfolioAsset.name || portfolioAsset.ticker,
              signal_score: portfolioAsset.portfolio?.signalScore ?? null,
              ...formData,
            });
            setPortfolioAsset(null);
          }}
        />
      )}
    </div>
  );
}
